import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { CATEGORIES, type CategorySlug } from "@/lib/categoryLanding";
import { getCategoryLanding } from "@/lib/categoryLanding.functions";
import { pageHead } from "@/lib/routeHead";
import CategoryLanding from "@/components/inventory/CategoryLanding";

const BASE = "https://www.rexandsonsrvs.com";

export const landingQuery = (slug: CategorySlug) =>
  queryOptions({
    queryKey: ["category-landing", slug],
    queryFn: () => getCategoryLanding({ data: slug }),
    staleTime: 60_000,
  });

/** Shared route options for the indexable category landing pages. */
export function categoryRouteOptions(slug: CategorySlug) {
  const cfg = CATEGORIES[slug];
  function Page() {
    const { data } = useSuspenseQuery(landingQuery(slug));
    return <CategoryLanding cfg={cfg} units={data.units} unavailable={data.unavailable} />;
  }
  return {
    loader: ({ context }: { context: { queryClient: import("@tanstack/react-query").QueryClient } }) =>
      context.queryClient.ensureQueryData(landingQuery(slug)),
    head: ({ loaderData }: { loaderData?: { units: { slug: string; title: string }[] } }) => {
      const base = pageHead({ title: cfg.title, description: cfg.description, path: `/${slug}` });
      return {
        meta: [...base.meta, { name: "robots", content: "index, follow" }],
        links: base.links,
        scripts: [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
                { "@type": "ListItem", position: 2, name: "RVs for Sale", item: `${BASE}/rv-search` },
                { "@type": "ListItem", position: 3, name: cfg.h1, item: `${BASE}/${slug}` },
              ],
            }),
          },
          ...(loaderData?.units.length
            ? [{
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "ItemList",
                  itemListElement: loaderData.units.map((u, i) => ({ "@type": "ListItem", position: i + 1, url: `${BASE}/rv-search/${u.slug}`, name: u.title })),
                }),
              }]
            : []),
        ],
      };
    },
    component: Page,
    errorComponent: () => <CategoryLanding cfg={cfg} units={[]} unavailable />,
    notFoundComponent: () => <CategoryLanding cfg={cfg} units={[]} unavailable={false} />,
  };
}
